import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
} from "@mui/material";

const statusOptions = [
  { value: "upcoming", label: "Upcoming" },
  { value: "active", label: "Active" },
  { value: "locked", label: "Locked" },
  { value: "completed", label: "Completed" },
];

const CreateMatchdayModal = ({
  open,
  onClose,
  tournamentId,
  existingMatchdayNumbers = [],
  onSubmit,
}) => {
  const [formData, setFormData] = useState({
    matchdayNumber: "",
    name: "",
    expectedMatchCount: 4,
    status: "upcoming",
    budgetInitial: "",
  });

  const [errors, setErrors] = useState({});

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        matchdayNumber: "",
        name: "",
        expectedMatchCount: 4,
        status: "upcoming",
        budgetInitial: "",
      });
      setErrors({});
    }
  }, [open]);

  const validate = () => {
    const newErrors = {};

    if (!formData.matchdayNumber || formData.matchdayNumber < 1) {
      newErrors.matchdayNumber =
        "Matchday number is required and must be at least 1";
    } else if (
      existingMatchdayNumbers.includes(parseInt(formData.matchdayNumber))
    ) {
      newErrors.matchdayNumber =
        "Matchday number already exists for this tournament";
    }

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.expectedMatchCount || formData.expectedMatchCount < 1) {
      newErrors.expectedMatchCount = "Expected match count must be at least 1";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({
        ...formData,
        matchdayNumber: parseInt(formData.matchdayNumber),
        expectedMatchCount: parseInt(formData.expectedMatchCount),
        budgetInitial: formData.budgetInitial
          ? parseInt(formData.budgetInitial)
          : undefined,
      });
      onClose();
    }
  };

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create New Matchday</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Matchday Number *"
                type="number"
                value={formData.matchdayNumber}
                onChange={handleChange("matchdayNumber")}
                error={!!errors.matchdayNumber}
                helperText={
                  errors.matchdayNumber || "Unique number for this matchday"
                }
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Expected Match Count *"
                type="number"
                value={formData.expectedMatchCount}
                onChange={handleChange("expectedMatchCount")}
                error={!!errors.expectedMatchCount}
                helperText={
                  errors.expectedMatchCount ||
                  "How many matches will be in this matchday"
                }
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Matchday Name *"
                value={formData.name}
                onChange={handleChange("name")}
                error={!!errors.name}
                helperText={
                  errors.name || 'e.g., "Matchday 1 - Group Stage Openers"'
                }
                placeholder="Enter matchday name"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.status}>
                <InputLabel>Status *</InputLabel>
                <Select
                  value={formData.status}
                  onChange={handleChange("status")}
                  label="Status *"
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Initial Budget"
                type="number"
                value={formData.budgetInitial}
                onChange={handleChange("budgetInitial")}
                helperText="Team budget for this matchday (e.g. 50000000)"
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Auto-Calculated Dates:</strong> Start time, end time,
                and lock time will be automatically calculated based on the
                matches you assign to this matchday.
              </Alert>
              <Alert severity="info">
                <strong>Status Guide:</strong>
                <br />• <strong>Upcoming:</strong> Matchday is scheduled but not
                yet active
                <br />• <strong>Active:</strong> Users can make transfers and
                changes
                <br />• <strong>Locked:</strong> Transfers are closed, matches
                are live or about to start
                <br />• <strong>Completed:</strong> All matches finished, points
                calculated
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            Create Matchday
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateMatchdayModal;
